import React, { useState, useEffect } from 'react';
import { getRecipes, createRecipe, updateRecipe, deleteRecipe, createRecipesBatch } from '../api';
import RecipeForm from './RecipeForm';
import MealTypeMultiSelect from './MealTypeMultiSelect';
import './RecipesView.css';

const MEAL_TYPES = ['desayuno', 'almuerzo', 'comida', 'cena'];

const RecipesView = () => {
  const [recipes, setRecipes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mealTypeFilters, setMealTypeFilters] = useState([]);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favoriteRecipes, setFavoriteRecipes] = useState(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const stored = window.localStorage.getItem('favoriteRecipes');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch (error) {
      console.warn('No se pudieron cargar favoritos desde localStorage', error);
      return new Set();
    }
  });

  useEffect(() => {
    loadRecipes();
  }, [searchTerm]);

  const loadRecipes = async () => {
    setLoading(true);
    try {
      const response = await getRecipes(searchTerm);
      setRecipes(response.data);
    } catch (error) {
      console.error('Error cargando recetas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRecipe(null);
    setShowForm(true);
  };

  const handleEdit = (recipe) => {
    setEditingRecipe(recipe);
    setShowForm(true);
  };

  const toggleFavorite = (id) => {
    setFavoriteRecipes((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem('favoriteRecipes', JSON.stringify(Array.from(updated)));
        } catch (error) {
          console.warn('No se pudieron guardar los favoritos', error);
        }
      }
      return updated;
    });
  };

  const hasActiveFilters = mealTypeFilters.length > 0 || favoritesOnly;

  const clearFilters = () => {
    setMealTypeFilters([]);
    setFavoritesOnly(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta receta?')) {
      try {
        await deleteRecipe(id);
        loadRecipes();
      } catch (error) {
        console.error('Error eliminando receta:', error);
        alert('Error al eliminar la receta');
      }
    }
  };

  const handleFormSubmit = async (recipeData) => {
    try {
      if (editingRecipe) {
        await updateRecipe(editingRecipe.id, recipeData);
      } else {
        await createRecipe(recipeData);
      }
      setShowForm(false);
      setEditingRecipe(null);
      loadRecipes();
    } catch (error) {
      console.error('Error guardando receta:', error);
      alert('Error al guardar la receta');
    }
  };

  const handleBatchSubmit = async (jsonText) => {
    try {
      // Parsear el JSON
      const jsonData = JSON.parse(jsonText);
      
      if (!Array.isArray(jsonData)) {
        alert('El JSON debe ser un array de recetas');
        return;
      }

      const normalizeCalories = (value) => {
        if (value === undefined || value === null || value === '') return null;
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      };

      // Convertir el formato JSON al formato del backend
      const recipes = jsonData.map(item => {
        // Convertir meal_type de "Desayuno" a "desayuno" (minúsculas)
        const mealTypeMap = {
          'Desayuno': 'desayuno',
          'Almuerzo': 'almuerzo',
          'Comida': 'comida',
          'Cena': 'cena'
        };
        
        const mealType = mealTypeMap[item.meal_type] || item.meal_type.toLowerCase();
        
        // Convertir array de ingredientes a string separado por punto y coma
        let ingredientsList = [];
        
        if (Array.isArray(item.ingredients)) {
          ingredientsList = [...item.ingredients];
        } else if (item.ingredients) {
          ingredientsList = [item.ingredients];
        }
        
        // Agregar contenido de "others" si existe y tiene contenido
        if (Array.isArray(item.others) && item.others.length > 0) {
          ingredientsList = ingredientsList.concat(item.others);
        } else if (item.others && typeof item.others === 'string') {
          ingredientsList.push(item.others);
        }
        
        const ingredients = ingredientsList.join('; ');
        
        return {
          title: item.title,
          meal_type: mealType,
          calories: normalizeCalories(item.calories),
          ingredients: ingredients
        };
      });

      if (recipes.length === 0) {
        alert('No se encontraron recetas válidas en el JSON');
        return;
      }

      await createRecipesBatch(recipes);
      setShowBatchForm(false);
      loadRecipes();
      alert(`${recipes.length} recetas creadas exitosamente`);
    } catch (error) {
      console.error('Error creando recetas en batch:', error);
      if (error instanceof SyntaxError) {
        alert('Error: El JSON no es válido. Por favor, verifica el formato.');
      } else {
        alert('Error al crear las recetas en batch: ' + error.message);
      }
    }
  };

  const filteredRecipes = recipes.filter((recipe) => {
    const normalizedMealType = (recipe.meal_type || '').toLowerCase();
    const matchesMealType =
      mealTypeFilters.length === 0 || mealTypeFilters.includes(normalizedMealType);

    const isFavorite = favoriteRecipes.has(recipe.id);
    const matchesFavorite = !favoritesOnly || isFavorite;

    return matchesMealType && matchesFavorite;
  });

  const sortedRecipes = [...filteredRecipes].sort((a, b) => {
    const favA = favoriteRecipes.has(a.id) ? 1 : 0;
    const favB = favoriteRecipes.has(b.id) ? 1 : 0;
    if (favB !== favA) {
      return favB - favA;
    }
    return a.title.localeCompare(b.title);
  });

  return (
    <div className="recipes-view">
      <div className="recipes-header">
        <h2>Gestión de Recetas</h2>
        <div className="recipes-actions">
          <button className="btn-primary" onClick={() => setShowBatchForm(true)}>
            Cargar en Batch
          </button>
          <button className="btn-primary" onClick={handleCreate}>
            Nueva Receta
          </button>
        </div>
      </div>

      <div className="recipes-search">
        <input
          type="text"
          placeholder="Buscar receta..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="filters-row">
          <div className="meal-type-filter">
            <MealTypeMultiSelect
              options={MEAL_TYPES}
              value={mealTypeFilters}
              onChange={setMealTypeFilters}
            />
          </div>
          <label className="favorites-filter">
            <input
              type="checkbox"
              checked={favoritesOnly}
              onChange={(e) => setFavoritesOnly(e.target.checked)}
            />
            Favoritos
          </label>
          {hasActiveFilters && (
            <button
              className="btn-icon btn-clear-filters"
              onClick={clearFilters}
              aria-label="Limpiar filtros"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Cargando recetas...</div>
      ) : (
        <div className="recipes-list">
          {sortedRecipes.length === 0 ? (
            <div className="no-recipes">No se encontraron recetas</div>
          ) : (
            sortedRecipes.map((recipe) => {
              const isFavorite = favoriteRecipes.has(recipe.id);
              return (
                <div key={recipe.id} className="recipe-card">
                  <div className="recipe-card-header">
                    <h3>{recipe.title}</h3>
                    <div className="recipe-actions">
                      <button
                        className={`icon-btn btn-favorite ${isFavorite ? 'active' : ''}`}
                        onClick={() => toggleFavorite(recipe.id)}
                        aria-label={isFavorite ? 'Quitar de favoritos' : 'Marcar como favorito'}
                      >
                        {isFavorite ? '♥' : '♡'}
                      </button>
                      <button
                        className="icon-btn btn-edit"
                        onClick={() => handleEdit(recipe)}
                        aria-label="Editar receta"
                      >
                        ✏️
                      </button>
                      <button
                        className="icon-btn btn-delete"
                        onClick={() => handleDelete(recipe.id)}
                        aria-label="Eliminar receta"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                <div className="recipe-details-info">
                  <div><strong>Tipo:</strong> {recipe.meal_type ? recipe.meal_type.charAt(0).toUpperCase() + recipe.meal_type.slice(1) : 'N/A'}</div>
                  {recipe.calories && <div><strong>Calorías:</strong> {recipe.calories} kcal</div>}
                </div>
                <div className="recipe-ingredients">
                  <strong>Ingredientes:</strong>
                  <ul>
                    {recipe.ingredients
                      .split(';')
                      .map((ingredient) => ingredient.trim())
                      .filter((ingredient) => ingredient.length > 0)
                      .map((ingredient, index) => (
                        <li key={`${recipe.id}-ingredient-${index}`}>{ingredient}</li>
                      ))}
                  </ul>
                </div>
              </div>
              );
            })
          )}
        </div>
      )}

      {showForm && (
        <RecipeForm
          recipe={editingRecipe}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingRecipe(null);
          }}
        />
      )}

      {showBatchForm && (
        <BatchRecipeForm
          onSubmit={handleBatchSubmit}
          onCancel={() => setShowBatchForm(false)}
        />
      )}
    </div>
  );
};

const BatchRecipeForm = ({ onSubmit, onCancel }) => {
  const [jsonText, setJsonText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(jsonText);
  };

  const exampleJson = `[
  {
    "meal_type": "Comida",
    "title": "CABALLA & ARROZ Y VERDURA",
    "ingredients": [
      "150gr de caballa.",
      "50gr arroz.",
      "Salteado de berenjena en dados con pimiento en tiras, sal y ajo en polvo al gusto.",
      "1 cuch. (9gr) aceite oliva virgen extra",
      "Pieza de fruta de temporada"
    ],
    "others": []
  },
  {
    "meal_type": "Cena",
    "title": "GUISANTES FRANCESES",
    "ingredients": [
      "100gr de taquitos de jamón.",
      "200gr guisantes cocidos con zanahoria baby, salteado con cebolla troceada y ajo en polvo al gusto.",
      "1 cuch. (9gr) aceite oliva virgen extra o mantequilla (estilo francés)",
      "1 yogur entero natural. (yogur de leche o de soja)",
      "1 onza (8gr) de chocolate negro"
    ],
    "others": ["Hacer virutas y mezclar con el yogur."]
  }
]`;

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Cargar Recetas en Batch (JSON)</h3>
          <button className="btn-close" onClick={onCancel}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="batch-form">
          <div className="form-group">
            <label>
              Pega aquí el JSON con las recetas. El formato debe ser un array de objetos con:
              <br />
              - <strong>meal_type</strong>: "Desayuno", "Almuerzo", "Comida" o "Cena"
              <br />
              - <strong>title</strong>: Título de la receta
              <br />
              - <strong>ingredients</strong>: Array de strings con los ingredientes
              <br />
              - <strong>others</strong>: Array opcional con información adicional (se agregará a los ingredientes)
              <br />
              - <strong>calories</strong>: Valor numérico opcional con las calorías
            </label>
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              rows={25}
              className="batch-textarea"
              placeholder={exampleJson}
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Cargar Recetas
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecipesView;
